<template>
  <div class="reseller-apply">
    <!-- ── HERO ─────────────────────────────────────────────────── -->
    <div class="apply-hero">
      <div class="hero-glow hero-glow-a"></div>
      <div class="hero-glow hero-glow-b"></div>

      <v-container fluid class="pa-4 pa-sm-6">
        <div class="hero-inner text-center">
          <div class="hero-emblem">
            <v-icon dark size="34">mdi-cart-arrow-down</v-icon>
          </div>
          <h1 class="hero-title">Become a Reseller</h1>
          <p class="hero-subtitle">
            Resell verified seller products and earn on every sale.
          </p>

          <div class="hero-chips">
            <div class="hero-chip">
              <v-icon small left color="amber lighten-2">mdi-sale</v-icon>
              Earn per sale
            </div>
            <div class="hero-chip">
              <v-icon small left color="amber lighten-2">mdi-shield-check</v-icon>
              Verified products
            </div>
            <div class="hero-chip">
              <v-icon small left color="amber lighten-2">mdi-wallet</v-icon>
              Cash out anytime
            </div>
          </div>
        </div>
      </v-container>
    </div>

    <v-container fluid class="pa-4 pa-sm-6 apply-container">
      <v-layout row justify-center>
        <v-flex xs12 sm10 md8 lg7>

          <!-- Loading -->
          <div v-if="loading">
            <v-card rounded="xl" elevation="0" class="panel-card pa-4">
              <v-skeleton-loader
                v-for="n in 3"
                :key="n"
                type="card"
                class="mb-3"
              ></v-skeleton-loader>
            </v-card>
          </div>

          <!-- Error -->
          <div v-else-if="error">
            <v-card rounded="xl" elevation="0" class="panel-card pa-6 text-center">
              <div class="state-icon error-state"><v-icon size="30">mdi-cloud-alert</v-icon></div>
              <h3 class="state-title mt-3">Something went wrong</h3>
              <p class="state-text">{{ error }}</p>
              <v-btn color="indigo darken-3" dark pill elevation="2" class="btn-gradient mt-2" @click="loadStatus">
                <v-icon left small>mdi-refresh</v-icon>
                Retry
              </v-btn>
            </v-card>
          </div>

          <!-- Approved -->
          <div v-else-if="isReseller">
            <transition name="fade-slide" appear>
              <v-card rounded="xl" elevation="0" class="panel-card pa-6 text-center">
                <div class="success-burst">
                  <div class="state-icon success-state">
                    <v-icon size="34">mdi-check-decagram</v-icon>
                  </div>
                </div>
                <h2 class="state-title">You're an approved reseller!</h2>
                <p class="state-text">
                  Pick products from the catalog and start earning.
                </p>
                <v-btn
                  large
                  pill
                  dark
                  color="indigo darken-3"
                  :to="dashboardLink"
                  elevation="3"
                  class="cta-btn btn-gradient"
                >
                  <v-icon left small>mdi-view-dashboard</v-icon>
                  Go to Dashboard
                </v-btn>
              </v-card>
            </transition>
          </div>

          <!-- Pending -->
          <div v-else-if="hasApplication && application.status === 'PENDING'">
            <transition name="fade-slide" appear>
              <v-card rounded="xl" elevation="0" class="panel-card pa-6">
                <div class="text-center mb-4">
                  <div class="pending-spinner-wrap">
                    <v-progress-circular indeterminate color="amber" size="46" width="4"></v-progress-circular>
                  </div>
                  <h2 class="state-title mt-3">Application Under Review</h2>
                  <p class="state-text mb-0">
                    Submitted on {{ formatDate(application.created_at) }} — we'll notify you as soon as it's approved.
                  </p>
                </div>

                <v-timeline dense class="review-timeline">
                  <v-timeline-item color="success" small fill-dot left>
                    <div class="timeline-label"><span class="font-weight-bold">Submitted</span></div>
                    <div class="caption grey--text">{{ formatDate(application.created_at) }}</div>
                  </v-timeline-item>
                  <v-timeline-item color="amber" small fill-dot left>
                    <div class="timeline-label font-weight-bold">Under review</div>
                    <div class="caption grey--text">A manager is checking your application</div>
                  </v-timeline-item>
                  <v-timeline-item color="grey lighten-2" small left>
                    <div class="timeline-label grey--text">Approved</div>
                    <div class="caption grey--text">You'll be notified here & by email</div>
                  </v-timeline-item>
                </v-timeline>
              </v-card>
            </transition>
          </div>

          <!-- Application form -->
          <div v-else>
            <transition name="fade-slide" appear>
              <div>
                <!-- Rejected notice -->
                <v-alert
                  v-if="hasApplication && application.status === 'REJECTED'"
                  type="error"
                  rounded="xl"
                  dense
                  class="mb-4"
                >
                  <div class="font-weight-bold">Application rejected</div>
                  <div v-if="application.rejection_reason" class="caption mt-1">
                    Reason: {{ application.rejection_reason }}
                  </div>
                </v-alert>

                <!-- How it works -->
                <div class="steps-row">
                  <div class="step-card">
                    <div class="step-icon"><v-icon color="indigo">mdi-store-search</v-icon></div>
                    <div class="step-label">1. Pick products</div>
                    <div class="step-text">Browse sellers' catalog and add products to your resale list.</div>
                  </div>
                  <div class="step-card">
                    <div class="step-icon"><v-icon color="indigo">mdi-sale</v-icon></div>
                    <div class="step-label">2. Start reselling</div>
                    <div class="step-text">Share your links and earn on every sale.</div>
                  </div>
                  <div class="step-card">
                    <div class="step-icon"><v-icon color="indigo">mdi-bank-transfer</v-icon></div>
                    <div class="step-label">3. Get paid</div>
                    <div class="step-text">Earnings land in your reseller balance — transfer them anytime.</div>
                  </div>
                </div>

                <!-- Confirm panel -->
                <v-card rounded="xl" elevation="0" class="panel-card confirm-card">
                  <div class="confirm-head">
                    <div class="confirm-icon"><v-icon color="indigo">mdi-account-check</v-icon></div>
                    <div>
                      <h3 class="confirm-title">
                        {{ hasApplication ? 'Resubmit your application' : 'You are one step away' }}
                      </h3>
                      <p class="confirm-sub">Confirm your contact details — they're recovered from your account.</p>
                    </div>
                  </div>

                  <v-divider class="my-4"></v-divider>

                  <div class="field-wrap">
                    <label class="field-label">Email address</label>
                    <v-text-field
                      v-model="form.email"
                      outlined
                      dense
                      rounded
                      solo-inverted="false"
                      class="rounded-field"
                      type="email"
                      placeholder="you@example.com"
                      prepend-inner-icon="mdi-email-outline"
                      :error-messages="emailError"
                      @blur="validateEmail"
                    >
                      <template v-if="isPrefilled(form.email, storeEmail)" v-slot:append>
                        <v-chip x-small class="soft-badge">
                          <v-icon x-small left>mdi-check-decagram</v-icon>
                          from your account
                        </v-chip>
                      </template>
                    </v-text-field>
                  </div>

                  <div class="field-wrap">
                    <label class="field-label">Phone number</label>
                    <v-text-field
                      v-model="form.phone_contact"
                      outlined
                      dense
                      rounded
                      class="rounded-field"
                      type="tel"
                      placeholder="6XX XX XX XX"
                      prepend-inner-icon="mdi-phone-outline"
                      :error-messages="phoneError"
                    >
                      <template v-if="isPrefilled(form.phone_contact, storePhone)" v-slot:append>
                        <v-chip x-small class="soft-badge">
                          <v-icon x-small left>mdi-check-decagram</v-icon>
                          from your account
                        </v-chip>
                      </template>
                    </v-text-field>
                  </div>

                  <div class="notice-row">
                    <v-icon small color="teal darken-1">mdi-shield-check-outline</v-icon>
                    <span>
                      A manager will review your application. You'll get an email confirmation right away.
                    </span>
                  </div>

                  <v-btn
                    large
                    block
                    pill
                    dark
                    color="indigo darken-3"
                    :loading="submitting"
                    :disabled="!formValid"
                    @click="submitForm"
                    class="cta-btn btn-gradient mt-5"
                    elevation="3"
                  >
                    <v-icon left small class="cta-check">mdi-check</v-icon>
                    Confirm &amp; Submit
                  </v-btn>

<v-btn text block small pill class="ghost-help mt-1" @click="showIntro = true">
                <span class="help-badge"><v-icon x-small>mdi-help</v-icon></span>
                What is a reseller?
              </v-btn>
                </v-card>
              </div>
            </transition>
          </div>

        </v-flex>
      </v-layout>
    </v-container>

    <!-- ── First-time popup ─────────────────────────────────────── -->
    <v-dialog v-model="showIntro" max-width="500px" persistent overlay-opacity="0.5">
      <v-card rounded="xl" class="intro-card">
        <div class="intro-banner">
          <div class="intro-banner-glow"></div>
          <div class="intro-emblem"><v-icon dark size="30">mdi-cart-arrow-down</v-icon></div>
          <h2 class="intro-banner-title">What is a Reseller?</h2>
          <p class="intro-banner-sub">Turn someone else's products into your own income.</p>
        </div>

        <v-card-text class="pt-5">
          <div class="intro-step" v-for="(step, idx) in introSteps" :key="idx">
            <div class="intro-icon" :class="'tint-' + (idx + 1)">
              <v-icon :color="step.color">{{ step.icon }}</v-icon>
            </div>
            <div>
              <div class="intro-title">{{ step.title }}</div>
              <div class="intro-text">{{ step.text }}</div>
            </div>
          </div>

          <v-alert type="info" dense text rounded="lg" class="mt-1">
            You only need to confirm your <b>email</b> and <b>phone number</b> — the rest is taken
            care of by our team.
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn pill dark color="indigo darken-3" elevation="2" class="btn-gradient continue-btn" @click="dismissIntro">
            I understand — Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ResellerApplication',

  data() {
    return {
      loading: true,
      error: null,
      isReseller: false,
      application: null,
      submitting: false,
      showIntro: false,
      introShown: false,
      dashboardLink: '/business-account/market_place/reseller/dashboard',
      form: { email: '', phone_contact: '' },
      emailError: '',
      phoneError: '',
      introSteps: [
        {
          icon: 'mdi-view-list',
          color: 'indigo',
          title: 'Pick products',
          text: 'Browse the sellers\u2019 catalog and choose the products you want to resell.',
        },
        {
          icon: 'mdi-share-variant',
          color: 'teal',
          title: 'Earn on every sale',
          text: 'Share your links and earn a commission on every product sold.',
        },
        {
          icon: 'mdi-wallet',
          color: 'amber darken-1',
          title: 'Get paid',
          text: 'Earnings are added to your reseller balance, transferable to your wallet or a campaign.',
        },
      ],
    }
  },

  computed: {
    hasApplication() {
      return !!(this.application && this.application.id)
    },
    storeEmail() {
      return this.$store?.state?.user_info?.user?.email
        || this.$store?.state?.email
        || ''
    },
    storePhone() {
      return this.$store?.state?.user_info?.user_profile?.phone_number
        || this.$store?.state?.user_info?.user_profile?.transaction_phone
        || ''
    },
    formValid() {
      return this.form.email.trim().length > 0 && this.form.phone_contact.trim().length > 0
    },
    introSeenKey() {
      const id = this.$store?.state?.user_info?.user?.id || 'anon'
      return `reseller_intro_seen_${id}`
    },
  },

  mounted() {
    this.introShown = localStorage.getItem(this.introSeenKey) === '1'
    this.loadStatus()
  },

  methods: {
    async loadStatus() {
      this.loading = true
      this.error = null
      try {
        const res = await axios.get('/api/v1/market_place/reseller/application/status/')
        this.isReseller  = res.data.is_reseller === true
        this.application = res.data.application || null

        if (!this.form.email) this.form.email = this.storeEmail
        if (!this.form.phone_contact) this.form.phone_contact = this.storePhone

        if (!this.isReseller && !this.hasApplication && !this.introShown) {
          this.showIntro = true
        }
      } catch {
        this.error = 'Unable to load status. Please try again.'
      } finally {
        this.loading = false
      }
    },

    dismissIntro() {
      this.introShown = true
      localStorage.setItem(this.introSeenKey, '1')
      this.showIntro = false
    },

    isPrefilled(value, source) {
      return value && value.trim() === source
    },

    validateEmail() {
      const value = this.form.email.trim()
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        this.emailError = 'Please enter a valid email address.'
      } else {
        this.emailError = ''
      }
    },

    async submitForm() {
      const email = this.form.email.trim()
      const phone = this.form.phone_contact.trim()
      this.emailError = ''
      this.phoneError = ''

      if (!email) {
        this.emailError = 'Email is required'
        return
      }
      if (!phone) {
        this.phoneError = 'Phone number is required'
        return
      }
      this.validateEmail()
      if (this.emailError) return

      this.submitting = true
      try {
        const res = await axios.post('/api/v1/market_place/reseller/apply/', {
          email: email,
          phone_contact: phone,
        })
        this.$store.commit('setSnackBarMessage', res.data.message || 'Application submitted!')
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('activateSnackBar', true)
        this.$router.push('/business-account/dashboard')
        await this.loadStatus()
      } catch (err) {
        const msg = err.response?.data?.error || 'Error submitting application'
        this.$store.commit('setSnackBarMessage', msg)
        this.$store.commit('setSnackBarColor', 'error')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.submitting = false
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return 'N/A'
      return new Date(dateStr).toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.reseller-apply {
  min-height: 100vh;
  background: linear-gradient(180deg, #eef2ff 0%, #f8fafc 260px);
}

/* ── Hero ─────────────────────────────────────────────────────── */
.apply-hero {
  position: relative;
  background: linear-gradient(135deg, #283593 0%, #3f51b5 55%, #5c6bc0 100%);
  overflow: hidden;
  color: #fff;
  border-bottom-left-radius: 28px;
  border-bottom-right-radius: 28px;
}
.hero-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.35;
}
.hero-glow-a { width: 340px; height: 340px; top: -120px; left: -80px; background: #7986cb; }
.hero-glow-b { width: 300px; height: 300px; bottom: -140px; right: -60px; background: #ffb74d; opacity: 0.25; }
.hero-inner { position: relative; padding: 22px 0 34px; }
.hero-emblem {
  width: 72px; height: 72px; margin: 0 auto 14px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
}
.hero-title {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.4px;
  margin: 0 0 6px;
}
.hero-subtitle { font-size: 15px; font-weight: 400; color: rgba(255, 255, 255, 0.85); margin: 0 0 18px; }
.hero-chips { display: flex; justify-content: center; flex-wrap: wrap; gap: 10px; }
.hero-chip {
  display: flex; align-items: center;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 600;
}

.apply-container { margin-top: 8px; }

/* ── Panels ───────────────────────────────────────────────────── */
.panel-card {
  border: 1px solid #e3e8f7;
  background: #fff;
  border-radius: 22px;
  overflow: hidden;
}
.state-icon {
  width: 74px; height: 74px; margin: 0 auto;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
}
.error-state { background: #ffebee; color: #c62828; }
.success-state { background: #dcfce7; color: #16a34a; border: 1px solid rgba(34, 197, 94, 0.25); }
.state-title { font-size: 22px; font-weight: 700; color: #1e293b; }
.state-text { color: #64748b; font-size: 14px; max-width: 420px; margin: 8px auto; }

.success-burst {
  position: relative;
  width: 74px; margin: 0 auto;
}
.success-burst::before {
  content: '';
  position: absolute; inset: -10px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(46,125,50,0.18) 0%, transparent 70%);
}

/* ── Pending timeline ─────────────────────────────────────────── */
.pending-spinner-wrap {
  width: 74px; height: 74px; margin: 0 auto;
  border-radius: 50%;
  background: #fff8e1;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid #ffe082;
}
.review-timeline { max-width: 360px; margin: 18px auto 0; }
.timeline-label { font-size: 13.5px; }

/* ── Steps row ────────────────────────────────────────────────── */
.steps-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 22px;
}
.step-card {
  background: #fff;
  border: 1px solid #e3e8f7;
  border-radius: 18px;
  padding: 18px 16px;
  text-align: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.step-card:hover { transform: translateY(-3px); box-shadow: 0 12px 24px rgba(63, 81, 181, 0.10); }
.step-icon {
  width: 46px; height: 46px; margin: 0 auto 10px;
  border-radius: 14px;
  background: rgba(63, 81, 181, 0.09);
  display: flex; align-items: center; justify-content: center;
}
.step-label { font-size: 13.5px; font-weight: 700; color: #1e293b; }
.step-text { font-size: 12px; color: #64748b; margin-top: 4px; line-height: 1.5; }

/* ── Confirm panel ────────────────────────────────────────────── */
.confirm-card { padding: 24px; }
.confirm-head { display: flex; align-items: flex-start; gap: 14px; }
.confirm-icon {
  width: 46px; height: 46px; min-width: 46px;
  border-radius: 14px;
  background: rgba(63, 81, 181, 0.09);
  display: flex; align-items: center; justify-content: center;
}
.confirm-title { font-size: 18px; font-weight: 700; color: #1e293b; }
.confirm-sub { font-size: 13px; color: #64748b; margin: 2px 0 0; }

.field-wrap { margin-bottom: 16px; }
.field-label { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px; display: block; }
.rounded-field { border-radius: 16px; }

/* Soft green badge */
.soft-badge {
  height: 24px !important;
  border-radius: 999px !important;
  background: linear-gradient(180deg, #a5f3c0 0%, #86efac 100%) !important;
  color: #14532d !important;
  border: 1px solid rgba(34, 197, 94, 0.35) !important;
  box-shadow: 0 2px 6px rgba(34, 197, 94, 0.18) !important;
  padding: 0 10px !important;
  font-weight: 700 !important;
  font-size: 10px !important;
  letter-spacing: 0.2px;
  text-transform: none !important;
}
.soft-badge .v-icon.v-icon--left {
  margin-right: 3px;
  font-size: 12px !important;
  color: #16a34a;
}

.notice-row {
  display: flex; align-items: flex-start; gap: 8px;
  background: #e0f2f1;
  border: 1px solid #b2dfdb;
  color: #00695c;
  border-radius: 12px;
  font-size: 12.5px;
  padding: 10px 12px;
}

.cta-btn { transition: transform 0.15s ease, box-shadow 0.15s ease; }
.cta-btn:not([disabled]):hover { transform: translateY(-1px); box-shadow: 0 10px 22px rgba(40, 53, 147, 0.35) !important; }

/* Gradient pill buttons */
.btn-gradient {
  background: linear-gradient(135deg, #3949ab 0%, #5c6bc0 100%) !important;
  border: none !important;
  border-radius: 999px !important;
  box-shadow: 0 8px 20px rgba(57, 73, 171, 0.35) !important;
  position: relative;
  overflow: hidden;
}
.btn-gradient::before {
  content: '';
  position: absolute;
  top: -6px; left: 12%;
  width: 34%; height: 60%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.40), rgba(255, 255, 255, 0));
  border-radius: 50%;
  filter: blur(7px);
  pointer-events: none;
}
.btn-gradient:not([disabled]):hover {
  background: linear-gradient(135deg, #303f9f 0%, #3f51b5 100%) !important;
  transform: translateY(-2px);
  box-shadow: 0 12px 26px rgba(57, 73, 171, 0.45) !important;
}
.continue-btn { min-width: 230px; }
.cta-check {
  width: 22px; height: 22px; margin-right: 8px !important;
  background: rgba(255, 255, 255, 0.22);
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 14px !important;
}

/* "What is a reseller?" ghost help pill */
.ghost-help {
  color: #3949ab !important;
  font-weight: 700 !important;
  font-size: 12.5px !important;
  letter-spacing: 0.2px;
  text-transform: none !important;
}
.ghost-help:hover { background: rgba(57, 73, 171, 0.08) !important; }
.help-badge {
  width: 20px; height: 20px; margin-right: 9px;
  border-radius: 50%;
  background: rgba(57, 73, 171, 0.12);
  border: 1px solid rgba(57, 73, 171, 0.30);
  display: inline-flex; align-items: center; justify-content: center;
}
.help-badge .v-icon { font-size: 12px !important; color: #3949ab; }

/* ── Intro dialog ─────────────────────────────────────────────── */
.intro-card { overflow: hidden; }
.intro-banner {
  position: relative;
  background: linear-gradient(135deg, #283593 0%, #3f51b5 55%, #5c6bc0 100%);
  color: #fff;
  padding: 30px 28px 26px;
  text-align: center;
  overflow: hidden;
}
.intro-banner-glow {
  position: absolute; width: 200px; height: 200px;
  top: -90px; right: -70px;
  border-radius: 50%;
  background: #7e8fe8; opacity: 0.35; filter: blur(50px);
}
.intro-emblem {
  width: 60px; height: 60px; margin: 0 auto 12px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex; align-items: center; justify-content: center;
}
.intro-banner-title { font-size: 22px; font-weight: 800; margin: 0 0 4px; }
.intro-banner-sub { font-size: 13px; color: rgba(255, 255, 255, 0.85); margin: 0; }

.intro-step { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 18px; }
.intro-icon {
  width: 44px; height: 44px; min-width: 44px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
}
.tint-1 { background: rgba(63, 81, 181, 0.10); }
.tint-2 { background: rgba(0, 150, 136, 0.10); }
.tint-3 { background: rgba(255, 179, 0, 0.12); }
.intro-title { font-weight: 700; font-size: 14.5px; color: #1e293b; }
.intro-text { font-size: 13px; color: #64748b; line-height: 1.5; margin-top: 2px; }

/* ── Animations ───────────────────────────────────────────────── */
.fade-slide-enter-active { transition: all 0.32s cubic-bezier(0.22, 1, 0.36, 1); }
.fade-slide-leave-active { transition: all 0.15s ease; }
.fade-slide-enter, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(14px);
}

@media (max-width: 600px) {
  .steps-row { grid-template-columns: 1fr; gap: 10px; }
  .hero-title { font-size: 24px; }
  .confirm-card { padding: 18px; }
}
</style>